import type { SaveMode, SavedTargetingsManager } from '@/state/experiments/useSavedTargetings';
import { useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from 'helpers/cn';
import { diffTargeting, flattenTargeting } from 'helpers/targeting-identity';
import { ConfirmDialog } from '@/components/Dialogs';
import { Input } from '@/components/ui/input';
import {
  MAX_SAVED_TARGETING_NAME_LENGTH,
  savedTargetingNameError,
  summarizeSavedRules,
  truncateName,
} from '@/state/experiments/saved-targeting-summary';
import { DiffRows, GroupRows } from './TargetingDiffRows';

type SaveTargetingDialogProps = { manager: SavedTargetingsManager; initialMode: SaveMode };

const MODES: { value: SaveMode; label: string }[] = [
  { value: 'new', label: 'Save as new' },
  { value: 'update', label: 'Update existing' },
];

const InfoLine = ({ children }: { children: React.ReactNode }) => (
  <p className="supporting flex items-start gap-2">
    <Info className="mt-0.5 size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
    <span>{children}</span>
  </p>
);

/**
 * Save the experiment's current targeting as a new entry, or overwrite an existing one with it.
 * Mounted only while open, so its state starts fresh each time.
 */
export const SaveTargetingDialog = ({ manager, initialMode }: SaveTargetingDialogProps) => {
  const { savedTargetings, currentRules, source } = manager;
  const canUpdate = savedTargetings.length > 0;
  const [mode, setMode] = useState<SaveMode>(canUpdate ? initialMode : 'new');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [updateId, setUpdateId] = useState<number | null>(source?.id ?? savedTargetings[0]?.id ?? null);

  const conditions = flattenTargeting(currentRules);
  const nameError = savedTargetingNameError({ name, list: savedTargetings });
  const target = savedTargetings.find((entry) => entry.id === updateId);
  const diff = target ? diffTargeting(target.rules, currentRules) : null;
  const unchanged = !!diff && diff.adds.length === 0 && diff.removes.length === 0;

  const confirm = async () => {
    if (mode === 'new') {
      setNameTouched(true);
      if (!name.trim() || nameError) return;
      await manager.saveNew(name);
      return;
    }
    if (target && !unchanged) await manager.updateExisting(target);
  };

  const disabled = mode === 'new' ? !name.trim() || !!nameError : !target || unchanged;
  const confirmLabel = mode === 'new' ? 'Save targeting' : `Update "${truncateName(target?.name ?? '')}"`;

  return (
    <ConfirmDialog
      open
      title="Save targeting"
      description="Reuse this experiment's targeting on other experiments on this website."
      onConfirm={confirm}
      onCancel={manager.closeDialog}
      disabled={disabled}
      confirmLabel={confirmLabel}
    >
      <div className="flex flex-col gap-4">
        <div
          role="radiogroup"
          aria-label="Save mode"
          className="bg-panel2 border-line grid grid-cols-2 gap-1 rounded-lg border p-1"
        >
          {MODES.map(({ value, label }) => {
            const isOff = value === 'update' && !canUpdate;
            const checked = mode === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                disabled={isOff}
                title={isOff ? 'Nothing saved on this website yet.' : undefined}
                onClick={() => setMode(value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
                    ev.preventDefault();
                    if (canUpdate) setMode(mode === 'new' ? 'update' : 'new');
                  }
                }}
                className={cn(
                  'focus-visible:ring-ring h-9 rounded-md text-body transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50',
                  checked ? 'bg-panel text-foreground shadow-sm' : 'text-dim hover:text-foreground',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {mode === 'new' ? (
          <>
            <div className="flex flex-col gap-2">
              <label htmlFor="saved-targeting-name" className="micro-label">
                Name
              </label>
              <Input
                id="saved-targeting-name"
                value={name}
                maxLength={MAX_SAVED_TARGETING_NAME_LENGTH + 20}
                onChange={(ev) => setName(ev.target.value)}
                onBlur={() => setNameTouched(true)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    void confirm();
                  }
                }}
                placeholder="e.g. Cart with products, mobile"
                aria-invalid={nameTouched && !!nameError}
                aria-describedby={nameTouched && nameError ? 'saved-targeting-name-error' : undefined}
                className="border-line bg-field text-foreground h-10 rounded-lg"
                autoFocus
              />
              {nameTouched && nameError ? (
                <p id="saved-targeting-name-error" className="text-danger text-micro">
                  {nameError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <p className="micro-label">What gets saved</p>
              <div className="border-line bg-panel2 rounded-lg border p-3">
                {conditions.length ? (
                  <GroupRows conditions={conditions} />
                ) : (
                  <p className="supporting">
                    No conditions, so it saves as running on all. Loading it clears an experiment's targeting.
                  </p>
                )}
              </div>
            </div>

            <InfoLine>
              Saved as a copy. Experiments that load it get their own copy, so changing or deleting it later never
              affects them.
            </InfoLine>
          </>
        ) : (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="micro-label mb-2">Overwrite</legend>
              <div className="border-line max-h-[19rem] overflow-y-auto rounded-lg border">
                {savedTargetings.map((entry) => (
                  <label
                    key={entry.id}
                    className={cn(
                      'border-line flex cursor-pointer items-center gap-3 border-b px-3 py-2.5 last:border-b-0',
                      entry.id === updateId && 'bg-brand-muted',
                    )}
                  >
                    <input
                      type="radio"
                      name="saved-targeting-overwrite"
                      checked={entry.id === updateId}
                      onChange={() => setUpdateId(entry.id)}
                      className="accent-brand size-4 shrink-0"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-foreground truncate text-body">{entry.name}</span>
                      <span className="supporting truncate">{summarizeSavedRules(entry.rules)}</span>
                    </span>
                    {entry.id === source?.id ? <span className="supporting shrink-0">Loaded here</span> : null}
                  </label>
                ))}
              </div>
            </fieldset>

            {target && diff ? (
              <div className="border-line bg-panel2 flex flex-col gap-2 rounded-lg border p-3" aria-live="polite">
                <p className="text-foreground text-body font-medium">Changes to "{target.name}"</p>
                {unchanged ? (
                  <p className="supporting">No changes. This saved targeting already matches.</p>
                ) : (
                  <DiffRows diff={diff} showKeeps />
                )}
              </div>
            ) : null}

            {target ? (
              <InfoLine>
                Experiments that already loaded "{target.name}" keep their own copy and are not changed.
              </InfoLine>
            ) : null}
          </>
        )}
      </div>
    </ConfirmDialog>
  );
};
